import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle2, XCircle, Loader2, Mail } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import BrandMark from '../components/BrandMark';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const VerifyEmail = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [status, setStatus] = useState('loading'); // 'loading' | 'success' | 'error' | 'already_verified'
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState('');
  const [subdomain, setSubdomain] = useState('');
  
  const token = searchParams.get('token');
  const userType = searchParams.get('type'); // 'partner' or 'client'
  const subdomainParam = searchParams.get('subdomain'); // for clients

  // H-12: Strip the verification token from the URL after capturing it,
  // so it doesn't leak via Referer / browser history / third-party scripts.
  useEffect(() => {
    if (typeof window !== 'undefined' && token && window.location.search) {
      try {
        window.history.replaceState({}, document.title, window.location.pathname);
      } catch (_e) { /* noop */ }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const verifyEmail = async () => {
      if (!token) {
        setStatus('error');
        setMessage('Invalid verification link. No token provided.');
        return;
      }

      try {
        let response;
        if (userType === 'partner') {
          response = await axios.post(`${API}/partner/verify-email`, { token });
        } else if (userType === 'client') {
          if (!subdomainParam) {
            setStatus('error');
            setMessage('Invalid verification link. Missing required parameters.');
            return;
          }
          response = await axios.post(`${API}/client/verify-email`, { 
            token, 
            subdomain: subdomainParam 
          });
          setSubdomain(subdomainParam);
        } else {
          setStatus('error');
          setMessage('Invalid verification link. Unknown user type.');
          return;
        }

        if (response.data.success) {
          if (response.data.already_verified) {
            setStatus('already_verified');
          } else {
            setStatus('success');
          }
          setEmail(response.data.email || '');
          setMessage(response.data.message);
        }
      } catch (error) {
        setStatus('error');
        setMessage(error.response?.data?.detail || 'Verification failed. The link may have expired.');
      }
    };

    verifyEmail();
  }, [token, userType, subdomainParam]);

  const handleGoToLogin = () => {
    if (userType === 'partner') {
      navigate('/');
    } else if (userType === 'client' && subdomain) {
      // Check if we're on a preview/localhost environment or production
      const hostname = window.location.hostname;
      const isPreview = hostname === 'localhost' || 
                        hostname === '127.0.0.1' || 
                        hostname.includes('preview.emergentagent.com') ||
                        hostname.includes('preview.emergent');
      
      // Always redirect to the subdomain portal URL
      window.location.href = `https://${subdomain}.willkart.ai`;
    } else {
      navigate('/');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center px-4">
      <div className="max-w-md w-full">
        {/* Logo */}
        <div className="flex items-center justify-center gap-3 mb-8">
          <BrandMark size="lg" textSize="text-2xl" />
        </div>

        {/* Verification Card */}
        <div className="bg-slate-900/50 border border-slate-800 rounded-2xl p-8 text-center">
          {status === 'loading' && (
            <div className="space-y-4">
              <div className="w-16 h-16 rounded-full bg-blue-500/20 flex items-center justify-center mx-auto">
                <Loader2 className="w-8 h-8 text-blue-400 animate-spin" />
              </div>
              <h2 className="text-xl font-bold text-white">Verifying Your Email</h2>
              <p className="text-slate-400">Please wait while we verify your email address...</p>
            </div>
          )}

          {status === 'success' && (
            <div className="space-y-4">
              <div className="w-16 h-16 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto">
                <CheckCircle2 className="w-8 h-8 text-emerald-400" />
              </div>
              <h2 className="text-xl font-bold text-white">Email Verified!</h2>
              <p className="text-slate-400">{message}</p>
              {email && (
                <div className="bg-slate-800/50 rounded-lg p-3 border border-slate-700">
                  <div className="flex items-center justify-center gap-2 text-sm">
                    <Mail className="w-4 h-4 text-emerald-400" />
                    <span className="text-slate-300">{email}</span>
                  </div>
                </div>
              )}
              <Button
                onClick={handleGoToLogin}
                className="w-full bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold mt-4"
                data-testid="go-to-login-btn"
              >
                Go to Login →
              </Button>
            </div>
          )}

          {status === 'already_verified' && (
            <div className="space-y-4">
              <div className="w-16 h-16 rounded-full bg-blue-500/20 flex items-center justify-center mx-auto">
                <CheckCircle2 className="w-8 h-8 text-blue-400" />
              </div>
              <h2 className="text-xl font-bold text-white">Already Verified</h2>
              <p className="text-slate-400">{message}</p>
              <Button
                onClick={handleGoToLogin}
                className="w-full bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold mt-4"
              >
                Go to Login →
              </Button>
            </div>
          )}

          {status === 'error' && (
            <div className="space-y-4">
              <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center mx-auto">
                <XCircle className="w-8 h-8 text-red-400" />
              </div>
              <h2 className="text-xl font-bold text-white">Verification Failed</h2>
              <p className="text-slate-400">{message}</p>
              <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700 text-left">
                <p className="text-slate-300 text-sm mb-2">What you can do:</p>
                <ul className="text-slate-400 text-sm space-y-1 list-disc list-inside">
                  <li>Try logging in and request a new verification link</li>
                  <li>Check if you have a newer verification email</li>
                  <li>Contact support if the problem persists</li>
                </ul>
              </div>
              <Button
                onClick={handleGoToLogin}
                variant="outline"
                className="w-full border-slate-600 text-slate-300 hover:bg-slate-800 mt-4"
              >
                Go to Login
              </Button>
            </div>
          )}
        </div>

        {/* Footer */}
        <p className="text-center text-slate-500 text-sm mt-6">
          Need help? Contact us at{' '}
          <a href="mailto:help@willkart.ai" className="text-amber-400 hover:text-amber-300">
            help@willkart.ai
          </a>
        </p>
      </div>
    </div>
  );
};

export default VerifyEmail;
